import { useCallback } from 'react';

import { useAuth } from './useAuth';
import { useIdleTimeout } from './useIdleTimeout';
import './IdleTimeoutGuard.css';

/**
 * The inactivity timeout, applied platform-wide to sessions that can reach financial data.
 *
 * SCOPED BY THE CATEGORY OF DATA, NOT BY FEATURE. Investors and admins both reach invested
 * capital, live position estimates, ROI, settlement figures and other people's payouts, so
 * both are covered wherever they are in the app rather than only on the pages that happen
 * to render a number. A buyer looking at their own ticket QR is not in that category and is
 * exempt: signing them out every fifteen minutes would be friction with nothing behind it.
 *
 * FULL RE-AUTHENTICATION, NOT A LOCK SCREEN. On timeout this calls the real logout, which
 * clears both httpOnly cookies server-side. An overlay drawn over a page that still holds
 * live data in memory, with a session the server would still honour, is theatre.
 *
 * The warning exists because idle is counted strictly: a person reading a settlement
 * breakdown without touching anything is idle by this definition, which is intended, and a
 * minute's notice makes that judgement cheap to be wrong about.
 */

const IDLE_ROLES = ['investor', 'admin'];

export function IdleTimeoutGuard() {
  const { user, logout } = useAuth();
  const enabled = Boolean(user && IDLE_ROLES.includes(user.role));

  const onTimeout = useCallback(() => {
    void logout().finally(() => {
      // Full reload rather than a client-side route change. It tears down every open SSE
      // stream and drops all in-memory financial data with the JS heap, which a redirect
      // inside the SPA would leave sitting there.
      window.location.assign('/login?reason=idle');
    });
  }, [logout]);

  const { warning, secondsRemaining, staySignedIn } = useIdleTimeout(enabled, onTimeout);

  if (!enabled || !warning) return null;

  return (
    <div className="idle-warn" role="alertdialog" aria-live="assertive"
      aria-labelledby="idle-warn-title">
      <div className="idle-warn__box glass-panel">
        <h2 className="idle-warn__title" id="idle-warn-title">Still there?</h2>
        <p className="idle-warn__body">
          You will be signed out in <strong className="mono">{secondsRemaining}</strong>{' '}
          seconds because this session has been inactive. Financial data is only kept on
          screen while someone is using it.
        </p>
        <button type="button" className="button button--primary" onClick={staySignedIn}>
          Stay signed in
        </button>
      </div>
    </div>
  );
}
